import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Search, LayoutDashboard,
  User, Users, CreditCard, BookmarkCheck, BarChart3, Radar, LogOut, Building2 } from "lucide-react";
import BrandLogo from "@/components/brand/BrandLogo";
import { FAMILY_ICONS } from "@/data/activation-icons";
import { useBrandBranding } from "@/hooks/use-brand-branding";
import { useSpace } from "@/hooks/use-space";
import { useAuth } from "@/hooks/use-auth";
import { useActivationAccess } from "@/hooks/use-activation-access";
import { FAMILIES } from "@/data/activation-families";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type NavItem = { label: string; to: string; icon: React.ElementType; comingSoon?: boolean; group?: string; sub?: boolean };

function getInitials(name: string | null | undefined): string {
  if (!name || !name.trim()) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AppSidebar = () => {
  const { space } = useSpace();
  const { signOut, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: profile } = useQuery({
    queryKey: ["profile-initials", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("full_name").eq("id", user!.id).maybeSingle();
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const initials = getInitials(profile?.full_name);
  const { data: branding, isLoading: brandingLoading } = useBrandBranding();
  const hasBrand = !!branding?.brandId;

  const { navFamilies } = useActivationAccess();
  const familyNav: NavItem[] = navFamilies.map((f) => ({
    label: FAMILIES[f].label,
    to: `/activate/${f}`,
    icon: FAMILY_ICONS[f],
    group: "Activate",
    sub: true,
  }));

  const brandNav: NavItem[] = [
    { label: "Discover", to: "/discover", icon: Search },
    { label: "Saved", to: "/saved", icon: BookmarkCheck },
    { label: "Campaigns", to: "/engage", icon: LayoutDashboard, group: "Activate" },
    ...familyNav,
  ];

  const communityNav: NavItem[] = [
    { label: "Saved", to: "/saved", icon: BookmarkCheck },
    { label: "Campaigns", to: "/engage", icon: LayoutDashboard },
  ];

  const navItems = space === "brand" ? brandNav : communityNav;
  const groupedNav = navItems.filter((item) => item.group);

  const isActive = (path: string) => {
    if (path.startsWith("#")) return false;
    if (path === "/discover") {
      return location.pathname === "/discover" || location.pathname.startsWith("/community/");
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <TooltipProvider delayDuration={200}>
      <aside className="fixed left-0 top-0 bottom-0 z-50 border-r border-border bg-sidebar w-36 border items-center justify-center flex flex-col">
        {/* Top: Logo */}
        <div className="h-14 px-4 border-b border-sidebar-border border-0 flex items-center justify-center py-[8px] my-[14px]">
         <Link to="/discover" className="pb-[12px]">
           <img src="/ernest-wordmark.png" alt="ernest" className="h-7 w-auto" />
         </Link>
        </div>

        {/* Brand chip — pinned at top, above nav. Holds its space while loading so the icons do not jump. */}
        {brandingLoading && (
          <div className="w-full px-3 pb-0" aria-hidden>
            <div className="flex flex-col items-center gap-1.5 px-2 py-1.5">
              <Skeleton className="h-11 w-11 rounded-lg" />
              <Skeleton className="h-3.5 w-16" />
            </div>
          </div>
        )}
        {hasBrand && (
          <div className="w-full px-3 pb-0">
            <button
              onClick={() => navigate("/brand")}
              className="w-full flex flex-col items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors focus:outline-none group"
              aria-label="Your brand profile"
            >
              <BrandLogo
                src={branding?.logo}
                name={branding?.brandName}
                size={44}
                rounded="lg"
                primaryColor={branding?.primaryColor}
              />
              <span className="text-foreground truncate max-w-full text-[13px] text-center font-bold">
                {branding?.brandName || "Your brand"}
              </span>
            </button>
          </div>
        )}

        {/* Main Nav */}
        <nav className="flex-1 overflow-y-auto border-0 mt-0 py-[5px] px-[10px] w-full mb-[6px]" style={{ display: "flex", flexDirection: "column" }}>
          <div className="mt-9 mb-auto flex flex-col" style={{ gap: 6 }}>
          {navItems.filter((item) => !item.group).map((item) =>
            item.comingSoon ? (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      "flex flex-col items-center gap-0.5 py-1 rounded-md text-[13px] font-medium transition-colors text-center px-[16px] cursor-default opacity-50",
                      "text-muted-foreground"
                    )}
                  >
                    <item.icon size={18} className="shrink-0" />
                    <span>{item.label}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Coming Soon</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className="flex flex-col items-center gap-0.5 py-1 rounded-md text-[13px] font-bold text-foreground text-center px-[16px]"
              >
                <span className={cn("p-1.5 rounded-md", isActive(item.to) && "bg-[#deff00b1]")}>
                  <item.icon size={18} className="shrink-0" />
                </span>
                <span>{item.label}</span>
              </Link>
            )
          )}
          {groupedNav.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              <div className="mx-3 mb-1 h-px bg-border" aria-hidden />
              {groupedNav.filter((item) => !item.sub).map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex flex-col items-center gap-0.5 py-1 rounded-md text-[13px] font-bold text-foreground text-center px-[16px]"
                >
                  <span className={cn("p-1.5 rounded-md", isActive(item.to) && "bg-[#deff00b1]")}>
                    <item.icon size={18} className="shrink-0" />
                  </span>
                  <span>{item.label}</span>
                </Link>
              ))}
              <div className="flex flex-col" style={{ gap: 6 }}>
                {groupedNav.filter((item) => item.sub).map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex flex-col items-center gap-0.5 py-1 rounded-md text-[13px] font-normal text-foreground text-center px-[16px]"
                  >
                    <span className={cn("p-1.5 rounded-md", isActive(item.to) && "bg-[#deff00b1]")}>
                      <item.icon size={18} className="shrink-0" />
                    </span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          </div>
        </nav>

        {/* User profile dropdown — bottom-left of sidebar */}
        <div className="w-full px-3 flex items-center justify-center pb-[20px] pt-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="focus:outline-none">
                <Avatar className="cursor-pointer hover:opacity-80 transition-opacity" style={{ width: 42, height: 42 }}>
                  <AvatarFallback style={{ background: '#5b4fff', color: '#EEEEEE', fontSize: 13, fontWeight: 500, borderRadius: '50%' }}>{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-56 bg-popover">
              <DropdownMenuItem onClick={() => navigate("/brand")} className="flex items-center gap-2 cursor-pointer">
                <Building2 size={14} /> Your brand profile
              </DropdownMenuItem>
              <Separator className="my-1" />
              <DropdownMenuItem onClick={() => navigate("/account#profile")} className="flex items-center gap-2 cursor-pointer">
                <User size={14} /> My Account Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/account#team")} className="flex items-center gap-2 cursor-pointer">
                <Users size={14} /> Manage Team Members
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/account#billing")} className="flex items-center gap-2 cursor-pointer">
                <CreditCard size={14} /> Plan & Billing
              </DropdownMenuItem>
              <Separator className="my-1" />
              <DropdownMenuItem
                onClick={async () => {
                  await signOut();
                  navigate("/login");
                }}
                className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
              >
                <LogOut size={14} /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>


      </aside>
    </TooltipProvider>
  );
};

export default AppSidebar;